import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../modules/notifications/actions';
import { AUTOPARK_FORM_NAME, REQUEST_ERROR_MESSAGE, FIELDS_FOR_VALIDATE } from './constants';
import {
	getAutoparkInfoRequest,
	createAutoparkRequest,
	updateAutoparkRequest,
	deleteAutoparkRequest,
	getAutoparkCompaniesListRequest,
} from '../../api/requests';
import {
	GET_AUTOPARK_INFO,
	GET_AUTOPARK_COMPANIES_LIST,
	setAutoparkInfoAction,
	setAutoparkCompaniesListAction,
	setIsAutoparkInfoLoadingAction,
	setIsAutoparkCompaniesListLoadingAction,
	CREATE_AUTOPARK,
	setIsAutoparkCreatingAction,
	UPDATE_AUTOPARK,
	setIsAutoparkUpdatingAction,
	DELETE_AUTOPARK,
	setIsAutoparkDeletingAction,
} from './actions';
import { DISMISS_TIME } from '../../api/constants';
import { errorHandler } from '../../api/utils';
import { getDataForSaveSelector, getFormIsInvalidSelector } from './selectors';
import { getAutoparksAction } from '../autoparks/actions';
import { transformToValidationErrors } from './utils';

export function* getAutoparkInfoSaga({ payload: id }) {
	try {
		yield put(setIsAutoparkInfoLoadingAction(true));

		const { data, message, toast } = yield call(getAutoparkInfoRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}
			yield put(
				setAutoparkInfoAction({
					...data,
					tk_id: data.tk.id,
				}),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsAutoparkInfoLoadingAction(false));
	}
}

export function* getAutoparkCompaniesListSaga() {
	try {
		yield put(setIsAutoparkCompaniesListLoadingAction(true));

		const { data, message, toast } = yield call(getAutoparkCompaniesListRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setAutoparkCompaniesListAction(
					data.map(company => ({
						key: company.id,
						value: company.id,
						text: company.name,
					})),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsAutoparkCompaniesListLoadingAction(false));
	}
}

export function* createAutoparkSaga({ payload: { redirect, searchQuery } }) {
	try {
		yield put(setIsAutoparkCreatingAction(true));

		const dataForSave = yield select(getDataForSaveSelector());

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const { data, errors, message, toast } = yield call(createAutoparkRequest, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			redirect();

			yield put(getAutoparksAction({ searchQuery, noLoading: true }));
		} else {
			if (errors) {
				yield put(touch(AUTOPARK_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(AUTOPARK_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsAutoparkCreatingAction(false));
	}
}

export function* updateAutoparkSaga({ payload: { id, redirect, searchQuery } }) {
	try {
		yield put(setIsAutoparkUpdatingAction(true));

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForSaveSelector());

		const { data, errors, message, toast } = yield call(updateAutoparkRequest, id, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			yield put(setAutoparkInfoAction(data));

			redirect();

			yield put(getAutoparksAction({ searchQuery, noLoading: true }));
		} else {
			if (errors) {
				yield put(touch(AUTOPARK_FORM_NAME, ...FIELDS_FOR_VALIDATE));
				yield put(stopSubmit(AUTOPARK_FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsAutoparkUpdatingAction(false));
	}
}

export function* deleteAutoparkSaga({ payload: { id, redirect, searchQuery } }) {
	try {
		yield put(setIsAutoparkDeletingAction(true));

		const { data, message, toast } = yield call(deleteAutoparkRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			redirect();

			yield put(getAutoparksAction({ searchQuery, noLoading: true }));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsAutoparkDeletingAction(false));
	}
}

export default function* autoparkModalSaga() {
	yield takeEvery(GET_AUTOPARK_INFO, getAutoparkInfoSaga);
	yield takeEvery(GET_AUTOPARK_COMPANIES_LIST, getAutoparkCompaniesListSaga);
	yield takeEvery(CREATE_AUTOPARK, createAutoparkSaga);
	yield takeEvery(UPDATE_AUTOPARK, updateAutoparkSaga);
	yield takeEvery(DELETE_AUTOPARK, deleteAutoparkSaga);
}
